import { useEffect, useRef, useState } from "react";
import { searchService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import PlayerAvatar from "../common/PlayerAvatar";
import ClubCrest from "../common/ClubCrest";
import NationalityFlags from "../common/NationalityFlags";
import { readRecentPlayers } from "./recentlyViewed";
import {
  positionLabel,
  positionChipStyle,
  primaryDetailedPosition,
  marketValueLabel,
  searchRowToPlayer,
} from "./playerDisplay";

// Compare page — player selection: big selection card (desktop, frame 1a),
// compact slot bar above the grid (frames 1b–1f), mobile selection screen
// (frame 1h) and the shared inline search.
//
// Search reuses the platform search service with the debounce + AbortController
// + active-guard pattern from RecommendModal (the canonical clone).

const MAX_PLAYERS = 4;

// ---------------------------------------------------------------------------
// shared inline search
// ---------------------------------------------------------------------------

export const PlayerSearch = ({
  onPick,
  excludeIds = [],
  autoFocus = false,
  variant = "slot", // 'slot' (desktop big) | 'inline' (compact) | 'mobile'
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [open, setOpen] = useState(true);
  const debounced = useDebounce(query, 300);
  const abortRef = useRef(null);
  const containerRef = useRef(null);
  const excludeKey = excludeIds.join(",");

  useEffect(() => {
    const q = debounced.trim();
    if (!q) {
      setResults([]);
      setSearching(false);
      setSearchFailed(false);
      return undefined;
    }
    let active = true;
    setSearching(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    searchService
      .search({ type: "players", query: q, page: 1, per_page: 8 }, controller.signal)
      .then((res) => {
        if (!active) return;
        // searchService never rejects — a network/500 failure comes back as
        // {success:false}. An aborted request is a superseded one, not a
        // failure; everything else gets an honest error state instead of the
        // misleading "No players found".
        if (!res?.success) {
          if (controller.signal.aborted) return;
          setResults([]);
          setSearchFailed(true);
          return;
        }
        setSearchFailed(false);
        const excluded = new Set(excludeKey.split(",").filter(Boolean).map(Number));
        const players = res?.data?.results?.players || [];
        setResults(players.filter((p) => !excluded.has(Number(p.wyscout_id))));
      })
      .catch(() => {
        if (!active || controller.signal.aborted) return;
        setResults([]);
        setSearchFailed(true);
      })
      .finally(() => active && setSearching(false));
    return () => {
      active = false;
    };
  }, [debounced, excludeKey]);

  const pick = (row) => {
    setQuery("");
    setResults([]);
    setSearchFailed(false);
    onPick(searchRowToPlayer(row));
  };

  const showDropdown = variant !== "mobile" && open && query.trim().length > 0;

  // Dropdown dismissal: outside mousedown + Escape (the old page's
  // document-mousedown-on-container-ref pattern). Reopens on focus/typing.
  useEffect(() => {
    if (!showDropdown) return undefined;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showDropdown]);
  const inputClasses =
    variant === "slot"
      ? "h-[70px] rounded-xl"
      : variant === "inline"
        ? "h-[44px] rounded-[11px]"
        : "h-[48px] rounded-[13px]";

  const resultRow = (p) => {
    const chip = positionChipStyle({
      positionCode: p.position_code2 || p.position_code3,
      position: p.position,
      tp_detailed_position: p.tp_detailed_position,
    });
    const pos =
      primaryDetailedPosition(p.tp_detailed_position) ||
      p.position_code2 ||
      p.position_code3 ||
      p.position;
    if (variant === "mobile") {
      return (
        <div key={p.wyscout_id} className="flex items-center gap-[10px] px-3 py-[11px] border-b border-[#F4F7FA] last:border-b-0">
          <PlayerAvatar src={p.image_url} name={p.short_name || p.name} className="w-8 h-8 rounded-full flex-shrink-0 text-[11px]" />
          <div className="flex flex-col gap-[1px] min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold text-[#0F172A] truncate" data-no-translate="true">
              {p.short_name || p.name}
            </div>
            <div className="text-[11px] text-[#5A6879] truncate" data-no-translate="true">
              {[p.club?.name, pos, p.age !== null && p.age !== undefined ? `${p.age}` : null]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => pick(p)}
            className="px-3 py-2 rounded-[9px] bg-[#2283FF] text-white text-[11.5px] font-bold flex-shrink-0"
          >
            Add
          </button>
        </div>
      );
    }
    return (
      <button
        key={p.wyscout_id}
        type="button"
        onClick={() => pick(p)}
        className="w-full flex items-center gap-[10px] px-[10px] py-2 rounded-[9px] hover:bg-[#F5F8FC] text-left"
      >
        <PlayerAvatar src={p.image_url} name={p.short_name || p.name} className="w-[30px] h-[30px] rounded-full flex-shrink-0 text-[10.5px]" />
        <span className="flex flex-col gap-[1px] min-w-0 flex-1">
          <span className="text-[13px] font-semibold text-[#0F172A] truncate" data-no-translate="true">
            {p.short_name || p.name}
          </span>
          <span className="text-[11px] text-[#7A8798] truncate" data-no-translate="true">
            {p.club?.name || ""}
          </span>
        </span>
        {pos && (
          <span
            className="px-[6px] py-[2px] rounded-md text-[10.5px] font-bold flex-shrink-0"
            style={chip}
            data-no-translate="true"
          >
            {pos}
          </span>
        )}
        {p.age !== null && p.age !== undefined && (
          <span className="text-[11.5px] text-[#7A8798] tabular-nums w-[42px] text-right flex-shrink-0" data-no-translate="true">
            {p.age} yrs
          </span>
        )}
        <span className="px-[10px] py-1 rounded-lg bg-[#2283FF] text-white text-[11px] font-bold flex-shrink-0">
          Add
        </span>
      </button>
    );
  };

  const resultsBody = (padding) =>
    searching && !results.length ? (
      <div className={`${padding} py-3 text-[12px] text-[#8494A8]`}>Loading...</div>
    ) : searchFailed ? (
      <div className={`${padding} py-3 text-[12px] text-[#B4232A]`}>
        Search failed — try again
      </div>
    ) : results.length ? (
      results.map(resultRow)
    ) : (
      <div className={`${padding} py-3 text-[12px] text-[#8494A8]`}>
        {`No players found for "${query.trim()}"`}
      </div>
    );

  return (
    <div
      ref={containerRef}
      className={variant === "mobile" ? "flex flex-col gap-[10px]" : "relative"}
    >
      <div
        className={`flex items-center gap-[9px] px-[13px] bg-white border border-[#2283FF] shadow-[0_0_0_3px_rgba(34,131,255,.12)] ${inputClasses}`}
      >
        <span className="text-[13px] text-[#9AA7B8]">⌕</span>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search players"
          autoFocus={autoFocus}
          className="flex-1 min-w-0 text-[13.5px] text-[#0F172A] outline-none bg-transparent placeholder-[#9AA7B8]"
        />
      </div>

      {showDropdown && (
        <div className="absolute top-full mt-2 left-0 w-[min(430px,90vw)] bg-white border border-[#E4E9F0] rounded-[13px] shadow-[0_22px_44px_-18px_rgba(16,24,40,.3)] p-[6px] z-20">
          <div className="px-[10px] pt-2 pb-[6px] text-[10px] font-bold tracking-[.09em] uppercase text-[#9AA7B8]">
            Search results
          </div>
          {resultsBody("px-[10px]")}
        </div>
      )}

      {variant === "mobile" && query.trim().length > 0 && (
        <div className="bg-white border border-[#E4E9F0] rounded-[13px] overflow-hidden">
          {resultsBody("px-3")}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// building blocks
// ---------------------------------------------------------------------------

const AvatarWithCrest = ({ player, size = 44 }) => (
  <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
    <PlayerAvatar
      src={player.imageUrl}
      name={player.name}
      className="w-full h-full rounded-full text-[13px]"
    />
    <ClubCrest name={player.club || ""} logoUrl={player.clubImage} variant="badge" avatarSize={size} />
  </div>
);

const RecentStrip = ({ onPick, excludeIds, horizontal = false }) => {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    setRecent(readRecentPlayers().filter((p) => !excludeIds.includes(p.id)));
  }, [excludeIds]);
  if (!recent.length) return null;
  return (
    <div
      className={
        horizontal
          ? "flex flex-col gap-[7px]"
          : "flex items-center gap-[10px] pt-3 border-t border-[#F1F5F9]"
      }
    >
      <span className="text-[10px] font-bold tracking-[.08em] uppercase text-[#9AA7B8] flex-shrink-0">
        Recently viewed
      </span>
      <div className={horizontal ? "flex gap-[7px] overflow-x-auto pb-[2px]" : "flex gap-[7px] flex-wrap"}>
        {recent.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p)}
            className="flex items-center gap-[7px] pl-[5px] pr-[10px] py-[5px] border border-[#E7ECF3] rounded-[20px] bg-[#FCFDFE] hover:border-[#BCCBDD] flex-shrink-0"
          >
            <PlayerAvatar src={p.imageUrl} name={p.name} className="w-[22px] h-[22px] rounded-full text-[9px]" />
            <span className="text-[11.5px] font-semibold text-[#0F172A] whitespace-nowrap" data-no-translate="true">
              {p.shortName || p.name}
            </span>
            {positionLabel(p) && (
              <span className="text-[10.5px] text-[#9AA7B8]" data-no-translate="true">
                {positionLabel(p)}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const SlotCard = ({ player, onRemove }) => {
  const chip = positionChipStyle(player);
  const pos = positionLabel(player);
  const mv = marketValueLabel(player.marketValue);
  return (
    <div className="flex items-center gap-[11px] p-3 border border-[#E7ECF3] rounded-xl bg-[#FCFDFE] min-w-0">
      <AvatarWithCrest player={player} size={44} />
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-[#0F172A] truncate" data-no-translate="true">
          {player.name}
        </div>
        <div className="text-[11.5px] text-[#7A8798] truncate" data-no-translate="true">
          {player.club || ""}
        </div>
        <div className="flex flex-wrap gap-[5px] items-center">
          {pos && (
            <span className="px-[6px] py-[2px] rounded-md text-[10.5px] font-bold" style={chip} data-no-translate="true">
              {pos}
            </span>
          )}
          {player.age !== null && player.age !== undefined && (
            <span className="px-[6px] py-[2px] rounded-md bg-[#F1F4F9] text-[#3F4E62] text-[10.5px] font-semibold" data-no-translate="true">
              {player.age} yrs
            </span>
          )}
          {/* current market value — chip appears once the profile loads,
              omitted entirely when the value is missing or 0 */}
          {mv && (
            <span
              className="px-[6px] py-[2px] rounded-md bg-[#F1F4F9] text-[#3F4E62] text-[10.5px] font-semibold"
              title="Market value"
              data-no-translate="true"
            >
              {mv}
            </span>
          )}
          <NationalityFlags
            nationalities={player}
            max={2}
            className="w-[17px] h-3 object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(15,23,42,.08)]"
            wrapperClassName="flex items-center gap-1"
          />
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="ml-auto w-6 h-6 rounded-md flex items-center justify-center text-[11px] text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569] flex-shrink-0"
      >
        ✕
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// desktop: big selection card (frame 1a) — shown until the grid opens
// ---------------------------------------------------------------------------

export const PlayerSelectionCard = ({ players, onAdd, onRemove }) => {
  const excludeIds = players.map((p) => p.id);
  const emptySlots = MAX_PLAYERS - players.length;
  return (
    <div className="bg-white border border-[#E4E9F0] rounded-2xl p-4 flex flex-col gap-[14px] shadow-[0_1px_2px_rgba(16,24,40,.04)]">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 items-start">
        {players.map((p) => (
          <SlotCard key={p.id} player={p} onRemove={() => onRemove(p.id)} />
        ))}
        {emptySlots > 0 && <PlayerSearch onPick={onAdd} excludeIds={excludeIds} variant="slot" />}
        {Array.from({ length: Math.max(0, emptySlots - 1) }, (_, i) => (
          <div
            key={`empty-${i}`}
            className="flex items-center justify-center gap-[7px] h-[70px] border border-dashed border-[#CFD9E6] rounded-xl text-[#8494A8] text-[12.5px] font-semibold"
          >
            <span className="text-[14px] leading-none">+</span>
            <span>Add player</span>
          </div>
        ))}
      </div>
      <RecentStrip onPick={onAdd} excludeIds={excludeIds} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// desktop: compact slot bar above the grid (frames 1b–1f)
// ---------------------------------------------------------------------------

export const PlayerSlotBar = ({ players, onAdd, onRemove, onReset }) => {
  const [addOpen, setAddOpen] = useState(false);
  const excludeIds = players.map((p) => p.id);
  const emptySlots = MAX_PLAYERS - players.length;

  const handleAdd = (player) => {
    setAddOpen(false);
    onAdd(player);
  };

  return (
    <div className="bg-white border border-[#E4E9F0] rounded-[14px] px-3 py-[10px] shadow-[0_1px_2px_rgba(16,24,40,.04)]">
      <div className="flex items-center gap-2 flex-wrap">
        {players.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-[9px] pl-[6px] pr-2 py-[6px] border border-[#E7ECF3] rounded-[11px] bg-[#FCFDFE] min-w-0"
          >
            <PlayerAvatar src={p.imageUrl} name={p.name} className="w-[30px] h-[30px] rounded-full text-[11px] flex-shrink-0" />
            <div className="flex flex-col gap-[1px] min-w-0">
              <div className="text-[13.5px] font-semibold text-[#0F172A] truncate max-w-[158px]" data-no-translate="true">
                {p.name}
              </div>
              <div className="text-[11.5px] text-[#5A6879] whitespace-nowrap" data-no-translate="true">
                {[positionLabel(p), p.age !== null && p.age !== undefined ? p.age : null]
                  .filter((x) => x !== null && x !== undefined && x !== "")
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onRemove(p.id)}
              aria-label="Remove"
              className="w-[18px] h-[18px] rounded-md flex items-center justify-center text-[11px] text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569] flex-shrink-0"
            >
              ✕
            </button>
          </div>
        ))}
        {emptySlots > 0 && (
          <button
            type="button"
            onClick={() => setAddOpen((v) => !v)}
            className="flex items-center gap-[7px] px-[14px] h-[44px] border border-dashed border-[#CFD9E6] rounded-[11px] text-[#8494A8] text-[12px] font-semibold hover:border-[#2283FF] hover:text-[#2283FF]"
          >
            <span className="text-[14px] leading-none">+</span>
            <span>Add player</span>
          </button>
        )}
        <div className="flex-1" />
        <div className="text-[11.5px] text-[#8494A8] font-semibold whitespace-nowrap">
          {players.length} of 4 players
        </div>
        <button
          type="button"
          onClick={onReset}
          className="text-[11.5px] text-[#64748B] font-semibold hover:text-[#1B6FD6] px-1 py-[6px]"
        >
          Reset
        </button>
      </div>
      {addOpen && emptySlots > 0 && (
        <div className="mt-[10px] max-w-[430px]">
          <PlayerSearch onPick={handleAdd} excludeIds={excludeIds} variant="inline" autoFocus />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// mobile: selection screen (frame 1h) — shown until the grid opens
// ---------------------------------------------------------------------------

export const MobileSelection = ({ players, onAdd, onRemove, onReset }) => {
  const excludeIds = players.map((p) => p.id);
  return (
    <div>
      <div className="bg-white border-b border-[#E4E9F0] px-3 py-3">
        <div className="flex items-center justify-between">
          <div className="text-[16px] font-bold tracking-[-.01em] text-[#0F172A]">Compare</div>
          <button type="button" onClick={onReset} className="text-[11.5px] font-semibold text-[#8494A8]">
            Reset
          </button>
        </div>
        <div className="text-[11.5px] text-[#8494A8] mt-[3px]">
          {players.length} of 4 players selected
        </div>
      </div>
      <div className="p-3 flex flex-col gap-[10px]">
        {players.map((p) => {
          const chip = positionChipStyle(p);
          const pos = positionLabel(p);
          return (
            <div key={p.id} className="flex items-center gap-[10px] p-[11px] bg-white border border-[#E7ECF3] rounded-[13px]">
              <AvatarWithCrest player={p} size={40} />
              <div className="flex flex-col gap-[3px] min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-[#0F172A] truncate" data-no-translate="true">
                  {p.name}
                </div>
                <div className="flex items-center gap-[5px] min-w-0">
                  <NationalityFlags
                    nationalities={p}
                    max={1}
                    className="w-4 h-[11px] object-cover rounded-[2px]"
                    wrapperClassName="flex items-center"
                  />
                  <span className="text-[11.5px] text-[#5A6879] truncate" data-no-translate="true">
                    {p.club || ""}
                  </span>
                </div>
              </div>
              {pos && (
                <span className="px-[6px] py-[2px] rounded-md text-[10px] font-bold flex-shrink-0" style={chip} data-no-translate="true">
                  {pos}
                </span>
              )}
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                aria-label="Remove"
                className="w-11 h-11 flex items-center justify-center text-[12px] text-[#A3B0C2] flex-shrink-0"
              >
                ✕
              </button>
            </div>
          );
        })}

        {players.length < MAX_PLAYERS && (
          <PlayerSearch onPick={onAdd} excludeIds={excludeIds} variant="mobile" />
        )}

        <RecentStrip onPick={onAdd} excludeIds={excludeIds} horizontal />

        {players.length < 2 && (
          <div className="flex flex-col items-center justify-center gap-[5px] h-[130px] border border-dashed border-[#D7DEE8] rounded-[14px] bg-white/60 text-center px-6">
            <div className="text-[13px] font-semibold text-[#5A6879]">Add one more player</div>
            <div className="text-[11.5px] text-[#8494A8] leading-[1.4]">
              The grid opens automatically at two players
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
